/**
 * HÌNH 3 – Chọn Thẩm phán cho vụ án (AI-assisted)
 * Bảng Thẩm phán với AI badge, row selection, [Phân công].
 */
import { useState } from 'react'
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tooltip,
  Radio,
} from '@mui/material'
import { mockJudgesForAssign } from '../../mock/data'
import { useGetCaseByIdQuery } from '../../store/api/casesApi'
import { useAssignJudgeMutation } from '../../store/api/casesApi'

const AI_LABELS = { fit: 'Phù hợp', overload: 'Quá tải – Không nên phân công', partial: 'Phù hợp một phần' }
const AI_COLORS = { fit: 'success' as const, overload: 'error' as const, partial: 'warning' as const }

export default function AssignJudgePage() {
  const { caseId } = useParams<{ caseId: string }>()
  const navigate = useNavigate()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const { data: caseDetail } = useGetCaseByIdQuery(caseId!, { skip: !caseId })
  const [assignJudge, { isLoading }] = useAssignJudgeMutation()

  const handleAssign = async () => {
    if (!caseId || !selectedId) return
    try {
      await assignJudge({ case_id: caseId, judge_id: selectedId }).unwrap()
      const judge = mockJudgesForAssign.find((j) => j.id === selectedId)
      navigate(`/workspace/assign?assigned=${caseId}&judgeName=${encodeURIComponent(judge?.name ?? '')}`)
    } catch {
      // error handled by mutation
    }
  }

  if (!caseId) return null
  const caseCode = caseDetail?.case_code ?? caseId
  const categoryLabel = caseDetail?.category === 'marriage_family' ? 'Hôn nhân gia đình' : caseDetail?.category ?? ''
  const filedDate = caseDetail?.filed_at ? new Date(caseDetail.filed_at).toLocaleDateString('vi-VN') : ''

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component={RouterLink} to="/workspace/assign" underline="hover" color="inherit">Phân công án</Link>
        <Typography color="text.primary">{caseCode}</Typography>
      </Breadcrumbs>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Chọn Thẩm phán cho vụ án {caseCode}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        {categoryLabel && <Chip label={categoryLabel} size="small" variant="outlined" />}
        {filedDate && <Chip label={`Nộp: ${filedDate}`} size="small" variant="outlined" />}
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
        <Table size="medium">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell padding="checkbox" />
              <TableCell><strong>Họ tên</strong></TableCell>
              <TableCell><strong>Đang giải quyết</strong></TableCell>
              <TableCell><strong>Tốc độ trung bình</strong></TableCell>
              <TableCell><strong>Chuyên môn</strong></TableCell>
              <TableCell><strong>AI Gợi ý</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mockJudgesForAssign.map((row) => (
              <TableRow
                key={row.id}
                hover
                selected={selectedId === row.id}
                onClick={() => setSelectedId(row.id)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: selectedId === row.id ? 'action.selected' : undefined,
                }}
              >
                <TableCell padding="checkbox">
                  <Radio checked={selectedId === row.id} size="small" />
                </TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
                <TableCell>{row.current_cases} vụ</TableCell>
                <TableCell>{row.avg_days_per_case} ngày/vụ</TableCell>
                <TableCell>{row.expertise.join('/')}</TableCell>
                <TableCell>
                  <Tooltip title={row.ai_tooltip}>
                    <Chip label={AI_LABELS[row.ai_suggestion]} size="small" color={AI_COLORS[row.ai_suggestion]} />
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate('/workspace/assign')}>Hủy</Button>
        <Button variant="contained" disabled={!selectedId || isLoading} onClick={handleAssign}>
          Phân công ✓
        </Button>
      </Box>
    </Box>
  )
}
